import type { WhatsNewRelease } from '../components/WhatsNewDialog';

/**
 * Release notes in the Buying Calculator. The acknowledgement is stored server-side
 * against their CEA, not in this browser.
 *
 * Newest first. The calculator opens these with `untilSuppressed`: they pop up on every
 * visit until the agent ticks "Don't show this again", and closing without ticking does
 * not count as seen. That tick opts an agent out of every future release, not just this
 * one. Still bump the newest `version` whenever the notes change.
 *
 * Keep entries in the agent's terms: what changed on screen and what to do differently,
 * not which component was refactored. Scope them to this calculator, and to what every
 * reader can actually reach — the Selling Calculator is access-gated, so a note about
 * the sale-proceeds handoff would land as a feature most agents cannot open.
 */
export const BUYING_WHATS_NEW: WhatsNewRelease[] = [
  {
    version: '2.1',
    date: 'October 2026',
    headline:
      'EC purchases now follow the bank\'s MSR / TDSR limits, with a Special Bridging Loan for HDB owners — and the progressive schedule pays out the way banks actually disburse.',
    items: [
      {
        title: 'EC loans capped by MSR and TDSR',
        body:
          'An EC loan is now limited by income, not just LTV.\n'
          + '• The bank lends the lower of 75% LTV and the loan quantum MSR (30%) and TDSR (55%) allow\n'
          + '• Any required loan above that shows as a Loan Shortfall, to be paid in cash or covered by fund pledging\n'
          + '• Tap the ⓘ on the loan for the working and the extra income needed to close the gap',
      },
      {
        title: 'Special Bridging Loan (EC Deferred Payment Scheme)',
        body:
          'For a buyer who still owns an HDB flat: answer "Do you own an existing HDB flat?" and enter its valuation, outstanding loan and resale levy.\n'
          + '• Up to 90% of the HDB valuation, less the outstanding loan and resale levy\n'
          + '• Paid out at TOP, so it reduces the bank loan — it cannot pay the 20% down payment\n'
          + '• Shows in its own Bridging column in the payment schedule',
      },
      {
        title: 'EC Scheme: Current vs New',
        body:
          'A Scheme switch on the EC tab. The calculator models the Current Scheme.\n'
          + 'Tap New Scheme to see what changes for EC sites tendered from 8 May 2026 — a 10-year MOP, NPS only and a larger first-timer allocation. It becomes selectable once those projects launch.',
      },
      {
        title: 'Loan Tenure: Normal or Stretched',
        body:
          'In Detailed mode the tenure starts at the maximum the buyer is eligible for.\n'
          + '• Tick Stretch to go past age 65 (up to 75) — LTV drops to 55%\n'
          + '• Tap the pencil to key in a shorter tenure\n'
          + '• A Normal vs Stretched card compares the tenure, max loan and max purchase price, and ticks the one in use',
      },
      {
        title: 'Progressive installments, as banks charge them',
        body:
          'For new launches and ECs, the loan tenure now starts at the first bank disbursement.\n'
          + '• At each drawdown the installment resets to repay the outstanding balance over the months left\n'
          + '• The monthly installment shown is the one after the last drawdown — tap ⓘ on any stage to see the working\n'
          + '• The PDF report uses the same figure',
      },
      {
        title: 'Edit any stage date',
        body:
          'Every milestone in the progressive schedule now takes its own date — Foundation, TOP, CSC and the rest, not just the OTP and S&P.\n'
          + 'Later stages never fall before earlier ones, and changing the OTP date clears your edits so the schedule recalculates from it.',
      },
    ],
  },
  {
    version: '2.0',
    date: 'September 2026',
    headline:
      'Each borrower now carries their own name and their own money — so funds and profiles stay attached to the right person.',
    items: [
      {
        title: 'Each borrower holds their own Cash and CPF',
        body:
          'Every borrower card now has Cash / Saving and CPF OA Balance, entered against that person.\n'
          + '• Available Funds → Cash and CPF are now totals of what each borrower declared — edit the figures on the cards\n'
          + '• Tap the ⓘ beside either total to see who contributed what\n'
          + '• Saving back to a profile group now writes each borrower\'s funds to their own profile, instead of skipping them because one total could not be split',
      },
      {
        title: 'Borrower names',
        body:
          'A Name field on each borrower card.\n'
          + '• Shows in the card header — "Primary Borrower - Adam Tan"\n'
          + '• Filled in automatically when you load a profile, and kept when you reload a saved case\n'
          + '• Used as the client\'s name when you save these inputs as a new profile',
      },
      {
        title: 'Save to History / Profile',
        body:
          'With no profile loaded, the save button now asks what you want:\n'
          + '• Save to History only — reopen this case later, as before\n'
          + '• Save to Profile — turn the borrowers into client profiles, without leaving the calculator\n'
          + '• Save to History and Profile — both, linked together\n'
          + 'Names come from the borrower cards; two borrowers create two profiles wrapped in a group, where you can set the group name and mark them as a married couple.',
      },
    ],
  },
];
